import { useQuery } from '@tanstack/react-query';
import { cashTransactionsApi } from '../api/cash-transactions.api';
import { useDebounce } from './useDebounce';

/**
 * Hook para estimar el fee de una operación cash-in o cash-out
 * dado un monto. Llama al endpoint de estimación del backend.
 * Incluye debounce de 600ms para evitar requests en cada tecla.
 *
 * @param type    - 'cash-in' | 'cash-out'
 * @param amount  - Monto a estimar (null/0 = deshabilitado)
 */
export const useFeeEstimate = (type: 'cash-in' | 'cash-out', amount: number | null) => {
  const debouncedAmount = useDebounce(amount, 600);
  const enabled = !!debouncedAmount && debouncedAmount > 0;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['fee-estimate', type, debouncedAmount],
    queryFn: () =>
      type === 'cash-in'
        ? cashTransactionsApi.estimateCashInFee(debouncedAmount!)
        : cashTransactionsApi.estimateCashOutFee(debouncedAmount!),
    enabled,
    staleTime: 30_000,
  });

  // Muestra spinner tanto durante debounce (amount ≠ debouncedAmount) como durante fetch
  const isPending = (!!amount && amount !== debouncedAmount) || (enabled && (isLoading || isFetching));

  return {
    estimate: data ?? null,
    isLoading: isPending,
    error,
  };
};
